// Pricing · Quote Builder — deal-desk. Two paths to compare (Quote vs Direct).
// List price computed LIVE by the backend engine (POST /pricing/compute); the
// off-list cross-check verdict drives the guardrail: Direct issuance is blocked
// off-list, PO routes to approval. Mirrors portal/pricing.jsx QuoteBuilder.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader, Card, CardHead, Mono, Btn } from '@/components/shared/primitives';
import { Pill } from '@/components/shared/badges';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { toast } from '@/components/shared/ToastProvider';
import { apiRequest } from '@/lib/supabase';
import { PERSONA } from '@/lib/persona';
import { getPriceBook, computePrice, makeFmt, type PriceBook, type ComputeResult } from '@/lib/api/pricing';

const SALES = PERSONA.sales.accent;
const selStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-primary)', boxSizing: 'border-box', background: 'var(--surface)' };

function PField({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>{label}</label>{children}</div>;
}
function PriceLine({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '7px 0', fontSize: 13 }}>
      <span style={{ color: 'var(--text-secondary)' }}>{label}{hint && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>{hint}</span>}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}
function Step({ n, children, done, warn }: { n: string; children: React.ReactNode; done?: boolean; warn?: boolean }) {
  const c = done ? '#16A34A' : warn ? '#D97706' : 'var(--text-muted)';
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 18, height: 18, borderRadius: '50%', background: c + '22', color: c, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{done ? '✓' : n}</span>{children}</div>;
}

interface OrgOption { id: string; name: string }

export function QuoteBuilder() {
  const navigate = useNavigate();
  const [pb, setPb] = useState<PriceBook | null>(null);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<'quote' | 'direct'>('quote');
  const [scope, setScope] = useState<'org' | 'platform'>('org');
  const [orgId, setOrgId] = useState('');
  const [tier, setTier] = useState('professional');
  const [vendorPool, setVendorPool] = useState(500);
  const [orgSeats, setOrgSeats] = useState(3);
  const [modules, setModules] = useState<string[]>(['sso']);
  const [packs, setPacks] = useState<Array<{ type: string; n: number }>>([]);
  const [years, setYears] = useState(1);
  const [cur, setCur] = useState('USD');
  const [fulfillment, setFulfillment] = useState<'po' | 'stripe'>('po');
  const [entered, setEntered] = useState('');
  const [result, setResult] = useState<ComputeResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      getPriceBook(),
      apiRequest<{ data?: OrgOption[] }>('/organizations').then((r) => r.data || []).catch(() => []),
    ]).then(([p, o]) => { setPb(p); setCur(p.baseCurrency || 'USD'); setOrgs(o); if (o[0]) setOrgId(o[0].id); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load pricing'));
  }, []);

  // Recompute list price live whenever the configuration changes.
  useEffect(() => {
    if (!pb) return;
    let cancelled = false;
    computePrice({ tier, vendorPool, orgSeats, modules, packs, scope, years, currency: cur })
      .then((r) => { if (!cancelled) setResult(r); })
      .catch(() => { if (!cancelled) setResult(null); });
    return () => { cancelled = true; };
  }, [pb, tier, vendorPool, orgSeats, modules, packs, scope, years, cur]);

  const fmt = useMemo(() => makeFmt(pb?.currencies || { USD: { sym: '$', rate: 1 } }), [pb]);
  const sym = pb?.currencies?.[cur]?.sym || '$';
  const floor = pb?.discountFloorPct ?? 15;

  const listInCur = result?.listInCurrency ?? 0;
  const enteredNum = parseFloat(entered);
  const hasEntered = !Number.isNaN(enteredNum);
  const offList = hasEntered && Math.abs(enteredNum - listInCur) > 0.5;
  const variancePct = hasEntered && listInCur > 0 ? Math.round((1 - enteredNum / listInCur) * 1000) / 10 : 0;
  const senior = Math.abs(variancePct) > floor;
  const needsApproval = offList || fulfillment === 'po';

  const toggleMod = (k: string) => setModules((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k]));
  const setPackN = (type: string, n: number) => setPacks((prev) => { const o = prev.filter((x) => x.type !== type); return n > 0 ? [...o, { type, n }] : o; });

  const submit = async () => {
    if (mode === 'direct' && offList) return;
    setSubmitting(true);
    try {
      await apiRequest('/quotes', {
        method: 'POST',
        body: JSON.stringify({
          organization_id: orgId || null, scope, tier,
          config: { vendorPool, orgSeats, modules, packs },
          currency: cur, term_years: years,
          list_price_monthly: result?.list.total ?? 0,
          quoted_price_monthly: hasEntered ? enteredNum : result?.listInCurrency ?? 0,
          fulfillment, mode,
        }),
      });
      toast({ kind: 'success', title: needsApproval ? 'Submitted for approval' : 'Quote created', body: 'The quote was saved to the deal-desk pipeline.' });
      navigate('/sales/quotes');
    } catch (e) {
      // Quotes persistence (A4) may not be wired yet — fail honestly, don't pretend.
      toast({ kind: 'warning', title: 'Quote not saved', body: e instanceof Error ? e.message : 'The quotes backend is not available yet.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (error || !pb) {
    return <div style={{ padding: 24 }}>{error ? <AlertBanner kind="error">{error}</AlertBanner> : <div style={{ color: 'var(--text-muted)' }}>Loading…</div>}</div>;
  }

  return (
    <div>
      <PageHeader
        breadcrumb={['Licenses', mode === 'quote' ? 'New Quote' : 'Issue License']}
        back onBack={() => navigate('/sales/quotes')}
        onCrumb={(i) => i === 0 && navigate('/sales/licenses')}
        title={mode === 'quote' ? 'New Quote' : 'Issue License'}
        subtitle={mode === 'quote' ? 'Build → validate → approve → provision' : 'Provision directly (on-list, no approval step)'}
      />

      {/* Mode compare toggle */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        {([['quote', 'Quote (deal desk)', 'Validated, approvable, audit trail — best for PO & discounts'], ['direct', 'Direct issuance', 'Fast path for on-list, card-paid deals']] as const).map(([k, label, blurb]) => (
          <button key={k} onClick={() => setMode(k)} style={{ flex: 1, textAlign: 'left', padding: '12px 16px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${mode === k ? SALES : 'var(--border)'}`, background: mode === k ? 'var(--ps-pill-green-bg)' : 'var(--surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, color: mode === k ? SALES : 'var(--text-primary)' }}>
              <Icon name={mode === k ? 'check-circle' : 'circle'} size={15} color={mode === k ? SALES : 'var(--text-muted)'} />{label}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, paddingLeft: 23 }}>{blurb}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, alignItems: 'start' }}>
        {/* Configure */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <CardHead title="Configuration" right={<Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">{pb.version}</Pill>} />
            <div style={{ padding: '16px 20px 0', display: 'flex', gap: 10 }}>
              {([['org', 'Org license (direct)', 'End customer · retail price'], ['platform', 'Platform license (reseller)', 'Partner resells · wholesale price']] as const).map(([k, l, b]) => (
                <button key={k} onClick={() => setScope(k)} style={{ flex: 1, textAlign: 'left', padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${scope === k ? SALES : 'var(--border)'}`, background: scope === k ? 'var(--ps-pill-green-bg)' : 'var(--surface)' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: scope === k ? SALES : 'var(--text-primary)' }}>{l}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{b}</div>
                </button>
              ))}
            </div>
            <div style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <PField label="Account">
                <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={selStyle}>
                  {orgs.length === 0 && <option value="">No organizations</option>}
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </PField>
              <PField label="Tier">
                <select value={tier} onChange={(e) => setTier(e.target.value)} style={selStyle}>
                  {pb.tiers.map((t) => <option key={t.tier} value={t.tier}>{t.name}</option>)}
                </select>
              </PField>
              <PField label="Vendor pool"><input type="number" value={vendorPool} onChange={(e) => setVendorPool(+e.target.value)} style={selStyle} /></PField>
              <PField label="Org seats"><input type="number" value={orgSeats} onChange={(e) => setOrgSeats(+e.target.value)} style={selStyle} /></PField>
              <PField label="Currency">
                <select value={cur} onChange={(e) => setCur(e.target.value)} style={selStyle}>
                  {Object.keys(pb.currencies).map((k) => <option key={k} value={k}>{pb.currencies[k].sym} {k}</option>)}
                </select>
              </PField>
              <PField label="Fulfillment">
                <div style={{ display: 'flex', gap: 6 }}>
                  {([['po', 'Purchase Order'], ['stripe', 'Card / Stripe']] as const).map(([k, l]) => (
                    <button key={k} onClick={() => setFulfillment(k)} style={{ flex: 1, padding: '8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, border: `1px solid ${fulfillment === k ? SALES : 'var(--border)'}`, background: fulfillment === k ? 'var(--ps-pill-green-bg)' : 'var(--surface)', color: fulfillment === k ? SALES : 'var(--text-secondary)' }}>{l}</button>
                  ))}
                </div>
              </PField>
              <PField label="Term (sold annually)">
                <select value={years} onChange={(e) => setYears(+e.target.value)} style={selStyle}>
                  {pb.terms.map((tm) => <option key={tm.years} value={tm.years}>{tm.years} year{tm.years > 1 ? 's' : ''}{tm.discount > 0 ? ` · -${Math.round(tm.discount * 100)}%` : ''}</option>)}
                </select>
              </PField>
            </div>
            <div style={{ padding: '0 20px 18px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Modules</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {pb.modules.map((m) => {
                  const on = modules.includes(m.key);
                  return <button key={m.key} onClick={() => toggleMod(m.key)} style={{ padding: '5px 11px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: on ? 600 : 500, border: `1px solid ${on ? SALES : 'var(--border)'}`, background: on ? 'var(--ps-pill-green-bg)' : 'var(--surface)', color: on ? SALES : 'var(--text-secondary)' }}>{m.name} · {fmt(m.price, cur)}</button>;
                })}
              </div>
            </div>
            <div style={{ padding: '0 20px 18px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Expansion packs</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pb.packs.map((p) => {
                  const sel = packs.find((x) => x.type === p.type); const n = sel ? sel.n : 0;
                  const sb: React.CSSProperties = { width: 24, height: 24, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: 14, lineHeight: 1, color: 'var(--text-secondary)', fontFamily: 'inherit' };
                  return (
                    <div key={p.type} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', border: `1px solid ${n > 0 ? SALES : 'var(--border)'}`, borderRadius: 8, background: n > 0 ? 'var(--ps-pill-green-bg)' : 'var(--surface)' }}>
                      <span style={{ fontSize: 12.5 }}>{p.name} · {fmt(p.price, cur)}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button onClick={() => setPackN(p.type, Math.max(n - 1, 0))} style={sb}>−</button>
                        <Mono style={{ width: 18, textAlign: 'center', fontWeight: 600 }}>{n}</Mono>
                        <button onClick={() => setPackN(p.type, n + 1)} style={sb}>+</button>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* PO / Stripe branch */}
          {fulfillment === 'po' ? (
            <Card>
              <CardHead title="Purchase order" sub="Activation policy varies by customer/terms" />
              <div style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <PField label="PO number"><input placeholder="PO-XXXXX" style={selStyle} /></PField>
                <PField label="Payment terms"><select style={selStyle}><option>Net 30</option><option>Net 60</option></select></PField>
                <PField label="Activate license"><select style={selStyle}><option>On PO number (net terms)</option><option>On PO document received</option><option>On finance approval</option></select></PField>
                <PField label="Invoice contact"><input placeholder="ap@account.com" style={selStyle} /></PField>
              </div>
              <div style={{ padding: '0 20px 16px', fontSize: 12, color: 'var(--text-muted)' }}>Activation options: on PO number (credit) · on PO document received · on finance approval.</div>
            </Card>
          ) : (
            <Card>
              <CardHead title="Card / Stripe" sub="Immediate subscription on success" />
              <div style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <PField label="Billing cycle"><select style={selStyle}><option>Monthly</option><option>Annual</option></select></PField>
                <PField label="Payment method"><select style={selStyle}><option>Collect at checkout</option></select></PField>
              </div>
            </Card>
          )}
        </div>

        {/* Live price cross-check */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <CardHead title="Price check" sub="Entered vs rate card" />
            <div style={{ padding: '16px 20px' }}>
              {result ? (
                <>
                  <PriceLine label="Base" value={fmt(result.list.base, cur)} />
                  <PriceLine label="Vendor seats" value={result.list.vendorSeatMrr ? fmt(result.list.vendorSeatMrr, cur) : '—'} hint={`${Math.max(vendorPool - (pb.tiers.find((t) => t.tier === tier)?.vendorIncluded || 0), 0)} over incl.`} />
                  <PriceLine label="Org seats" value={result.list.orgSeatMrr ? fmt(result.list.orgSeatMrr, cur) : '—'} hint={`${Math.max(orgSeats - (pb.tiers.find((t) => t.tier === tier)?.orgIncluded || 0), 0)} over incl.`} />
                  <PriceLine label="Modules" value={result.list.moduleMrr ? fmt(result.list.moduleMrr, cur) : '—'} />
                  <PriceLine label="Expansion packs" value={result.list.packMrr ? fmt(result.list.packMrr, cur) : '—'} />
                  {result.list.isPlatform && <PriceLine label="Retail subtotal" value={fmt(result.list.retail, cur)} />}
                  {result.list.isPlatform && <PriceLine label={`Wholesale −${result.list.wholesalePct}% (reseller)`} value={`−${fmt(result.list.retail - result.list.total, cur)}`} />}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0 4px', borderTop: '1px solid var(--border)', marginTop: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{result.list.isPlatform ? 'Wholesale / mo' : 'List price / mo'}</span>
                    <span style={{ fontSize: 20, fontWeight: 700 }}>{fmt(result.list.total, cur)}</span>
                  </div>
                  <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
                    <PriceLine label="Annual (×12)" value={fmt(result.term.annual, cur)} />
                    <PriceLine label={`Term discount · ${years}yr`} value={result.term.discount ? `−${Math.round(result.term.discount * 100)}%` : '—'} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 6, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>Annual net</span><span style={{ fontSize: 16, fontWeight: 700, color: SALES }}>{fmt(result.term.annualNet, cur)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
                      <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Contract value ({years}yr)</span><span style={{ fontSize: 12.5, fontWeight: 600 }}>{fmt(result.term.tcv, cur)}</span>
                    </div>
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Quoted price / mo ({cur})</label>
                    <input value={entered} onChange={(e) => setEntered(e.target.value)} placeholder={listInCur.toFixed(0)} style={{ ...selStyle, fontSize: 16, fontWeight: 700, borderColor: offList ? (senior ? 'var(--status-expired)' : '#D97706') : 'var(--border)' }} />
                  </div>

                  {hasEntered && (
                    <div style={{ marginTop: 12, padding: '11px 13px', borderRadius: 9, background: offList ? (senior ? 'var(--ps-pill-red-bg)' : 'var(--ps-pill-amber-bg)') : 'var(--ps-pill-green-bg)', border: `1px solid ${offList ? (senior ? 'var(--ps-pill-red-bg)' : 'var(--ps-pill-amber-bg)') : 'var(--ps-pill-green-bg)'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, color: offList ? (senior ? 'var(--ps-pill-red-tx)' : 'var(--ps-pill-amber-tx)') : 'var(--ps-pill-green-tx)' }}>
                        <Icon name={offList ? 'alert-triangle' : 'check-circle'} size={14} color={offList ? (senior ? 'var(--ps-pill-red-tx)' : 'var(--ps-pill-amber-tx)') : 'var(--ps-pill-green-tx)'} />
                        {offList ? (senior ? `${variancePct}% below list — senior approval required` : `${variancePct}% off list — needs approval`) : 'On list price — no discount approval needed'}
                      </div>
                      {offList && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 5 }}>List {fmt(result.list.total, cur)} · quoted {sym}{enteredNum.toLocaleString()} · variance {sym}{Math.abs(listInCur - enteredNum).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '8px 0' }}>Computing…</div>
              )}
            </div>
          </Card>

          {/* Next step */}
          <Card pad="16px 20px">
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Next step</div>
            {mode === 'quote' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                <Step n="1" done>Configure &amp; price-check</Step>
                <Step n="2" warn={needsApproval}>{needsApproval ? (offList ? 'Discount approval' : 'PO/terms approval') : 'Auto-approve (on-list)'}</Step>
                <Step n="3">Provision license from quote</Step>
                <Btn kind="primary" accent={SALES} icon="send" disabled={submitting} onClick={submit} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>{needsApproval ? 'Submit for approval' : 'Create & provision'}</Btn>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {offList ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ps-pill-amber-tx)' }}><Icon name="lock" size={14} color="var(--ps-pill-amber-tx)" />Direct issue is blocked for off-list pricing — switch to a Quote.</div>
                ) : (
                  <Step n="1" done>On-list — issue immediately</Step>
                )}
                <Btn kind="primary" accent={SALES} icon="file-plus-2" disabled={offList || submitting} onClick={submit} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>Issue License</Btn>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default QuoteBuilder;
